import { useState, useRef, useEffect } from "react";
import { MdOutlineDelete } from "react-icons/md";
import UploadImage from "../../../components/molecules/UploadImage";
import TextInput from "../../../components/molecules/inputs/TextInput";
import TextAreaInput from "../../../components/molecules/inputs/TextAreaInput";
import { IoMdAdd } from "react-icons/io";
import api from "../../../Api";
import axios from "axios";
import Skeleton from "react-loading-skeleton";

interface FAQItem {
  _id?: string;
  Icon: string;
  Title: string;
  Description: string;
}

export default function FAQ() {
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | string>("");
  const [inputValue, setInputValue] = useState("");
  const popUpRef = useRef<HTMLDivElement>(null);
  const [faqList, setFaqList] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [popUpLoading, setPopUpLoading] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState("");

  const handleImageUpload = (file: File) => {
    setUploadedImage(file);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleDescriptionChange = (value: string) => {
    setDescriptionValue(value);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popUpRef.current &&
        !popUpRef.current.contains(event.target as Node)
      ) {
        setIsPopUpOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchFaqList = async () => {
      try {
        const response = await axios.get(api.faq, {
          headers: {
            api_key: api.key,
            authentication: api.authantication,
          },
        });
        setFaqList(response.data.FAQ);
      } catch (error) {
        console.error("Error fetching FAQ:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFaqList();
  }, []);

  const handleSubmit = async () => {
    if (!uploadedImage || typeof uploadedImage === "string") {
      alert("Please upload an image.");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadedImage);

    try {
      setPopUpLoading(true);
      const uploadResponse = await axios.post(api.uploadFile, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (uploadResponse.data.success) {
        const uploadedImageUrl = uploadResponse.data.url;

        const faqItem: FAQItem = {
          Icon: uploadedImageUrl,
          Title: inputValue,
          Description: descriptionValue,
        };

        const response = await axios.post(api.faq, faqItem, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.data.success) {
          setFaqList((prevFaqList) => [...prevFaqList, faqItem]);
          setIsPopUpOpen(false);
          setPopUpLoading(false);
        } else {
          setPopUpLoading(false);
          console.warn("Failed to add FAQ item. Please try again.");
        }
      } else {
        console.error("Failed to upload image. Please try again.");
      }
    } catch (error) {
      console.error("Error adding FAQ item:", error);
    }
  };

  const handleDeleteFaqItem = async (faqId: string) => {
    try {
      await axios.delete(`${api.faq}/${faqId}`, {
        headers: {
          api_key: api.key,
          authentication: api.authantication,
        },
      });

      setFaqList((prevFaqList) =>
        prevFaqList.filter((faqItem) => faqItem._id !== faqId)
      );
    } catch (error) {
      console.error("Error deleting FAQ item:", error);
    }
  };

  return (
    <div>
      <div
        ref={popUpRef}
        className={`bg-white fixed top-0 z-10 w-[400px] border-l ${
          isPopUpOpen ? "right-0" : "right-[-500px]"
        } h-full p-4 duration-300 space-y-4`}
      >
        <div className="space-y-2">
          <h1 className="font-primary text-title">Add FAQ</h1>
          <p className="text-black/[.60]">
            Add FAQ to help customers find what they are looking for.
          </p>
        </div>

        <UploadImage ButtonText="Upload Icon" onUpload={handleImageUpload} />

        <TextInput
          Name="Title"
          Placeholder="Enter title for FAQ"
          Value={inputValue}
          onChange={handleInputChange}
        />

        <TextAreaInput
          Name="Description"
          Placeholder="Enter description of FAQ Question"
          Value={descriptionValue}
          onChange={handleDescriptionChange}
        />

        <div className="flex items-center justify-end space-x-4">
          <p
            onClick={() => setIsPopUpOpen(false)}
            className="text-black/[.60] cursor-pointer"
          >
            Cancel
          </p>
          <div
            onClick={handleSubmit}
            className="bg-primary text-white hover:bg-primary_dark duration-300 p-2 px-4 rounded hover:cursor-pointer"
          >
            <p>{popUpLoading ? "Submitting..." : "Submit"}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {loading
          ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={150} />
            ))
          : faqList.map((faq) => (
              <div
                key={faq._id}
                className="flex items-start gap-4 bg-white p-2 py-3 border rounded group cursor-pointer"
              >
                <div className="flex items-start pt-2 h-full w-[10%]">
                  <img
                    className="w-full object-cover"
                    src={faq.Icon}
                    alt="FAQ Icon"
                  />
                </div>
                <div className="w-[90%]">
                  <h1 className="font-primary capitalize">{faq.Title}</h1>
                  <p className="text-black/[.60] text-[14px]">
                    {faq.Description}
                  </p>
                </div>
                <div>
                  <MdOutlineDelete
                    onClick={() => handleDeleteFaqItem(faq._id!)}
                    className="text-[20px] group-hover:text-red-500 text-red-500/[.0] duration-300"
                  />
                </div>
              </div>
            ))}
      </div>

      <div
        onClick={() => setIsPopUpOpen(true)}
        className="bg-primary w-[46px] h-fit hover:w-[132px] p-3 text-[22px] text-white rounded-full fixed right-4 bottom-4 group duration-300 flex items-center gap-2 cursor-pointer"
      >
        <IoMdAdd />
        <p className="text-[16px] hidden h-[24px] pr-2 group-hover:flex duration-300">
          Add FAQ
        </p>
      </div>
    </div>
  );
}
